import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import MilestoneNotificationModal from '../Modals/MilestoneNotificationModal';

const { FiCalendar, FiHeart, FiStar, FiChevronRight, FiBell, FiSettings } = FiIcons;

const MilestoneWidget = ({ milestones, name }) => {
  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState(false);

  if (!milestones) return null;

  const today = new Date();
  const currentYear = today.getFullYear();

  const checkUpcoming = (dateStr) => {
    if (!dateStr) return { isUpcoming: false };
    const [month, day] = dateStr.split('-').map(Number);
    let targetDate = new Date(currentYear, month - 1, day);
    
    if (targetDate < today && (today.getTime() - targetDate.getTime()) > 86400000) {
      targetDate = new Date(currentYear + 1, month - 1, day);
    }

    const diffTime = targetDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    return {
      isUpcoming: diffDays >= 0 && diffDays <= 30,
      daysLeft: diffDays,
      date: targetDate
    };
  };

  const activeMilestones = [
    { id: 'heavenlyBirthday', label: 'Heavenly Birthday', icon: FiStar, dateStr: milestones.heavenlyBirthday },
    { id: 'weddingAnniversary', label: 'Wedding Anniversary', icon: FiHeart, dateStr: milestones.weddingAnniversary },
    { id: 'memorialAnniversary', label: 'Memorial Anniversary', icon: FiCalendar, dateStr: milestones.memorialAnniversary },
  ].map(m => ({ ...m, ...checkUpcoming(m.dateStr) }))
   .filter(m => m.isUpcoming)
   .sort((a, b) => a.daysLeft - b.daysLeft);

  if (activeMilestones.length === 0) return null;

  const next = activeMilestones[0];

  return (
    <>
      <motion.div 
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        className="bg-white p-8 rounded-[32px] shadow-sm border border-brand-dark relative overflow-hidden group"
      >
        <div className="absolute top-0 right-0 w-24 h-24 bg-accent-gold/5 rounded-bl-[100px] -mr-8 -mt-8 transition-transform group-hover:scale-110" />
        
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 bg-accent-gold/10 rounded-xl flex items-center justify-center text-accent-gold">
            <SafeIcon icon={next.icon} className="w-5 h-5" />
          </div>
          <div>
            <h3 className="font-serif text-xl text-accent-navy">Upcoming Milestone</h3>
            <p className="text-[10px] font-black uppercase tracking-widest text-text-muted">Honoring {name.split(' ')[0]}</p>
          </div>
        </div>

        <div className="space-y-6">
          <div className="p-5 bg-brand-light rounded-2xl border border-brand-dark/50 shadow-inner">
            <p className="text-[10px] font-black uppercase tracking-widest text-text-muted mb-1">{next.label}</p>
            <div className="flex justify-between items-end">
              <p className="text-xl font-serif text-accent-gold">
                {next.date.toLocaleDateString('en-US', { month: 'long', day: 'numeric' })}
              </p>
              <p className="text-[10px] font-black text-accent-navy bg-white px-3 py-1 rounded-full shadow-sm border border-brand-dark/20">
                {next.daysLeft === 0 ? 'Today' : `In ${next.daysLeft} days`}
              </p>
            </div>
          </div>

          <div className="space-y-3">
            <button 
              onClick={() => setIsModalOpen(true)}
              className="w-full btn-primary py-4 text-[10px] font-black uppercase tracking-widest flex items-center justify-center gap-2 group/btn"
            >
              <SafeIcon icon={FiBell} className="w-3.5 h-3.5 group-hover:animate-bounce" /> Configure Alerts
            </button>
            <button 
              onClick={() => navigate('/resources?tab=Funeral Planning')}
              className="w-full flex items-center justify-between p-4 rounded-xl border border-brand-dark hover:border-accent-gold hover:text-accent-gold transition-all group/btn bg-white"
            >
              <span className="text-[10px] font-black uppercase tracking-widest">Plan a Tribute</span>
              <SafeIcon icon={FiChevronRight} className="transition-transform group-hover/btn:translate-x-1" />
            </button>
          </div>
        </div>
      </motion.div>

      <MilestoneNotificationModal 
        isOpen={isModalOpen} 
        onClose={() => setIsModalOpen(false)} 
        name={name}
        milestones={milestones}
      />
    </>
  );
};

export default MilestoneWidget;